import { inject, Injectable } from '@angular/core';
import { Document, DocumentsService } from '../../features/documents/services/documents.service';
import { FileUploadFile, FileUploadInterface } from '../file-upload.interface';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService implements FileUploadInterface {
  private documentsService = inject(DocumentsService);

  async upload(file: File | Blob, fileName?: string): Promise<FileUploadFile> {
    // Use document-aware upload by default
    return this.uploadToDocuments(file, fileName);
  }

  async uploadToDocuments(file: File | Blob, fileName?: string, parentFolderId?: string): Promise<FileUploadFile> {
    const actualFile = file instanceof File ? file : new File([file], fileName || 'upload');
    // Use uploadFileToStorage which uploads directly to Supabase Storage
    // and creates the document record, avoiding FormData issues with edge functions
    const document = await this.documentsService.uploadFileToStorage(actualFile, parentFolderId);

    return this.convertDocumentToFileUploadFile(document);
  }

  async search(query: string): Promise<FileUploadFile[]> {
    // Use document search instead of basic storage search
    return this.searchDocuments(query);
  }

  async searchDocuments(query: string, folderId?: string): Promise<FileUploadFile[]> {
    return new Promise((resolve, reject) => {
      this.documentsService.searchDocuments(query).subscribe({
        next: (documents) => {
          const fileDocuments = documents.filter(doc => doc.type === 'file');
          const fileUploadFiles = fileDocuments.map(doc => this.convertDocumentToFileUploadFile(doc));
          resolve(fileUploadFiles);
        },
        error: (error) => reject(error)
      });
    });
  }

  async getRecentUploads(limit: number = 20): Promise<FileUploadFile[]> {
    return new Promise((resolve, reject) => {
      // Get all documents, filter for files, sort by created_at, and take the most recent
      this.documentsService.getDocumentTree().subscribe({
        next: (documents) => {
          const allFiles = this.getAllFiles(documents)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, limit);

          const fileUploadFiles = allFiles.map(doc => this.convertDocumentToFileUploadFile(doc));
          resolve(fileUploadFiles);
        },
        error: (error) => reject(error)
      });
    });
  }

  // Helper method to get files from specific folder
  async getFilesInFolder(folderId?: string): Promise<FileUploadFile[]> {
    return new Promise((resolve, reject) => {
      this.documentsService.getDocuments(folderId).subscribe({
        next: (documents) => {
          const fileDocuments = documents.filter(doc => doc.type === 'file');
          const fileUploadFiles = fileDocuments.map(doc => this.convertDocumentToFileUploadFile(doc));
          resolve(fileUploadFiles);
        },
        error: (error) => reject(error)
      });
    });
  }

  private convertDocumentToFileUploadFile(document: Document): FileUploadFile {
    return {
      id: document.id,
      name: document.name,
      type: document.mime_type || 'application/octet-stream',
      url: `/jaxfs/${document.id}`,
      documentId: document.id,
      parentFolderId: document.parent_id || undefined,
      createdAt: document.created_at,
      fileSize: document.file_size
    };
  }

  private getAllFiles(documents: Document[]): Document[] {
    const files: Document[] = [];

    for (const doc of documents) {
      if (doc.type === 'file') {
        files.push(doc);
      }
      if (doc.children && doc.children.length > 0) {
        files.push(...this.getAllFiles(doc.children));
      }
    }

    return files;
  }
}
